import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Rocket, Sparkles, Bot, BookOpen, Users, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { useWebsiteContent } from "@/contexts/WebsiteContentContext";
import { usePreview } from "@/contexts/PreviewContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useState } from "react";

const Hero = () => {
  const { content, isLoading } = useWebsiteContent();
  const { previewContent, isPreviewMode } = usePreview();
  const { language } = useLanguage();
  const lang = (language.startsWith('en') ? 'en' : 'ar') as 'en' | 'ar';
  const [showStats, setShowStats] = useState(false);

  // Use preview content when in preview mode, otherwise use regular content
  const activeContent = (isPreviewMode && previewContent) ? previewContent : content;

  // All hooks must be called before any early returns
  useEffect(() => {
    // Only animate stats cards
    const statsTimer = setTimeout(() => {
      setShowStats(true);
    }, 500);

    return () => {
      clearTimeout(statsTimer);
    };
  }, []);

  // Don't render if content is still loading
  if (isLoading) {
    return (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background pt-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading content...</p>
        </div>
      </section>
    );
  }

  const hero = activeContent?.hero;

  // Simplified safety check - only require hero section to exist
  if (!hero) {
    return (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background pt-24">
        <div className="text-center">
          <p className="text-muted-foreground">Content not available</p>
        </div>
      </section>
    );
  }

  const stats = [
    { value: hero.stats?.booksPublished?.value || '0', label: hero.stats?.booksPublished?.label?.[lang] || 'Books' },
    { value: hero.stats?.publishers?.value || '0', label: hero.stats?.publishers?.label?.[lang] || 'Publishers' },
    { value: hero.stats?.satisfaction?.value || '0%', label: hero.stats?.satisfaction?.label?.[lang] || 'Satisfaction' },
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-background pt-24">
      {/* Background Image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/abvi-background.png)',
          backgroundSize: '95%',
          backgroundPosition: '160% center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.5
        }}
      ></div>
      {/* Content */}
      <div className="container relative z-10 px-4 sm:px-6 lg:px-8 py-24 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-relaxed">
              <span className="text-foreground block">
                {hero.title?.line1?.[lang] || 'Transform Your Books Into'} <Bot className="inline h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-primary animate-bounce" />
              </span>
              <span className="bg-gradient-to-r from-primary via-purple-600 to-primary bg-clip-text text-transparent block pb-4 leading-normal">
                {hero.title?.line2?.[lang] || 'Digital Masterpieces'}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-foreground/75 mb-8 max-w-2xl leading-relaxed font-medium">
              {hero.subtitle?.[lang] || 'Professional eBook formatting and audiobook production for modern publishers.'}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 mb-12">
              <Button
                variant="hero"
                size="lg"
                className="text-lg px-10 py-4 h-auto font-bold rounded-full shadow-lg hover:shadow-2xl hover:scale-110 hover:-translate-y-1 transition-all duration-300 transform active:scale-105 group bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 border-0"
                asChild
              >
                <Link to="/publisher/uploadMetaData" className="flex items-center gap-2">
                  <Rocket className="h-5 w-5 group-hover:rotate-12 group-hover:scale-110 group-hover:-translate-y-1 transition-all duration-300" />
                  {hero.ctaButton?.[lang] || 'Start Your Journey'}
                  <Sparkles className="h-4 w-4 group-hover:scale-125 transition-all duration-300" style={{animation: 'spin 3s linear infinite'}} />
                </Link>
              </Button>

              <Button
                variant="hero-outline"
                size="lg"
                className="text-lg px-10 py-4 h-auto font-semibold rounded-full border-2 border-gray-400 text-foreground/70 hover:bg-gray-50 hover:shadow-xl hover:scale-110 hover:-translate-y-1 transition-all duration-300 transform active:scale-105 group"
                asChild
              >
                <a href="#how-it-works">
                  {hero.learnMoreButton?.[lang] || 'Learn More'}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                </a>
              </Button>
            </div>
          </div>

          {/* Right Stats Cards - Staggered Layout (Same Size) */}
          <div className="space-y-6">
            {/* Top Card - Books Published (Right Aligned) */}
            <div className="flex justify-end">
              <Card
                className={`p-10 border-2 border-gray-300 rounded-3xl bg-white shadow-lg hover:shadow-2xl hover:scale-105 hover:-translate-y-2 transition-all duration-1000 ease-out w-80 cursor-pointer group transform ${showStats ? 'translate-x-0 opacity-100' : 'translate-x-16 opacity-0'}`}
              >
                <div className="flex items-center gap-6 mb-3">
                  <div className="text-6xl font-bold text-foreground group-hover:scale-110 transition-transform duration-300">
                    {stats[0].value}
                  </div>
                  <BookOpen className="h-8 w-8 text-primary" />
                </div>
                <div className="text-base font-medium text-foreground/60">
                  {stats[0].label}
                </div>
              </Card>
            </div>

            {/* Middle Card - Publishers (Left Aligned) */}
            <div className="flex justify-start">
              <Card
                className={`p-10 border-2 border-gray-300 rounded-3xl bg-white shadow-lg hover:shadow-2xl hover:scale-105 hover:-translate-y-2 transition-all duration-1000 ease-out w-80 cursor-pointer group transform delay-300 ${showStats ? 'translate-x-0 opacity-100' : '-translate-x-16 opacity-0'}`}
              >
                <div className="flex items-center gap-6 mb-3">
                  <div className="text-6xl font-bold text-foreground group-hover:scale-110 transition-transform duration-300">
                    {stats[1].value}
                  </div>
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <div className="text-base font-medium text-foreground/60">
                  {stats[1].label}
                </div>
              </Card>
            </div>

            {/* Bottom Card - Satisfaction (Right Aligned, Purple) */}
            <div className="flex justify-end">
              <Card
                className={`p-10 border-0 rounded-3xl bg-primary text-white shadow-lg hover:shadow-2xl hover:scale-105 hover:-translate-y-2 transition-all duration-1000 ease-out w-80 cursor-pointer group transform delay-600 ${showStats ? 'translate-x-0 opacity-100' : 'translate-x-16 opacity-0'}`}
              >
                <div className="flex items-center gap-6 mb-3">
                  <div className="text-6xl font-bold text-white group-hover:scale-110 transition-transform duration-300">
                    {stats[2].value}
                  </div>
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <div className="text-base font-medium text-white/90">
                  {stats[2].label}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
